import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './dashboard.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   定数定義
   =============================================================== */

/** 日本語の曜日配列 */
const JWD = ['日', '月', '火', '水', '木', '金', '土'];

/* ===============================================================
   ヘルパー関数（日付・時刻フォーマット）
   =============================================================== */

/** 数値を2桁にゼロ埋め */
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Dateオブジェクトから 'YYYY/MM/DD' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列
 */
const fmtYMD = (d) => `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;

/**
 * Dateオブジェクトから 'YYYY/MM/DD(曜)' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列（曜日付き）
 */
const fmtYMDw = (d) => `${fmtYMD(d)}(${JWD[d.getDay()]})`;

/**
 * Dateオブジェクトから 'HH：MM' 形式の文字列を生成（全角コロン）
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた時刻文字列
 */
const fmtHMJP = (d) => `${pad2(d.getHours())}：${pad2(d.getMinutes())}`;

/**
 * 日付文字列と時刻文字列からDateオブジェクトを生成
 * @param {string} dStr - 日付文字列 (YYYY-MM-DD)
 * @param {string} tStr - 時刻文字列 (HH:MM または HH:MM:SS)
 * @returns {Date|null} - Dateオブジェクト、無効な場合はnull
 */
const toDateFromParts = (dStr, tStr) => {
  if (!dStr || !tStr) return null;
  const t = tStr.length === 5 ? `${tStr}:00` : tStr;
  const d = new Date(`${dStr}T${t}`);
  return isNaN(d) ? null : d;
};

/**
 * ISO形式の文字列からDateオブジェクトを生成
 * @param {string} iso - ISO形式の日時文字列
 * @returns {Date|null} - Dateオブジェクト、無効な場合はnull
 */
const toDateFromISO = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d;
};

/**
 * イベントオブジェクトから期間文字列を生成
 * 同日: YYYY/MM/DD(曜)　HH：MM～HH：MM
 * 別日: YYYY/MM/DD(曜)～YYYY/MM/DD(曜)　HH：MM～HH：MM
 * @param {Object} ev - イベントオブジェクト
 * @returns {string} - フォーマットされた期間文字列
 */
const formatPeriod = (ev) => {
  const { start_date, end_date, start_time, end_time, times_unified } = ev;
  if (!start_date) return '—';

  const isSingleDay = start_date === end_date;

  if (isSingleDay) {
    const s = toDateFromParts(start_date, start_time);
    const e = toDateFromParts(end_date, end_time);
    if (!s) return '—';
    if (!e) return `${fmtYMDw(s)}　${fmtHMJP(s)}`;
    return `${fmtYMDw(s)}　${fmtHMJP(s)}～${fmtHMJP(e)}`;
  }

  if (times_unified) {
    const s = toDateFromParts(start_date, start_time);
    const e = toDateFromParts(end_date, end_time);
    const timePart = (s && e) ? `　${fmtHMJP(s)}～${fmtHMJP(e)}` : '';
    return `${fmtYMDw(new Date(start_date))}～${fmtYMDw(new Date(end_date))}${timePart}`;
  }

  // 日程ごとに時刻が異なる場合は日付範囲のみ
  return `${fmtYMDw(new Date(start_date))}～${fmtYMDw(new Date(end_date))}`;
};

/* ===============================================================
   サブコンポーネント
   =============================================================== */
const Row = ({ item, onClick }) => {
  /**
   * バッジを決定
   * - participants に登録 & ステータスあり → そのステータスを表示（visibility 関係なく共通）
   * - participants に登録あるがステータス未設定:
   *     - 全体公開(出欠表示): 「未回答」
   *     - 招待制 / 全体公開: 「招待済み」
   * - participants に登録なし:
   *     - 全体公開(出欠表示): 「未回答」
   *     - 招待制 / 全体公開: バッジなし
   */
  const getBadge = () => {
    const vis = item.visibility;
    const status = item.participant_status;    // '参加' / '不参加' / 'キャンセル' / null
    const hasRecord = !!item.invite_status;    // participants に行があれば invite_status が何かしら入る

    // 明確なステータスがある場合は共通表示
    const STATUS_MAP = {
      '参加':       { label: '参加',       className: 'is-joined' },
      '不参加':     { label: '不参加',     className: 'is-declined' },
      'キャンセル': { label: 'キャンセル', className: 'is-cancel' },
    };
    if (status && STATUS_MAP[status]) {
      return STATUS_MAP[status];
    }

    // 以下はステータス未設定のケース
    if (vis === '全体公開(出欠表示)') {
      return { label: '未回答イベント', className: 'is-pending' };
    }

    // 招待制 / 全体公開
    if (hasRecord) {
      return { label: '未回答の招待イベント', className: 'is-invited' };
    }
    return null;
  };

  const badge = getBadge();

  const handleClick = () => {
    if (item.invite_token) {
      onClick(item.invite_token);
    }
  };

  return (
    <button
      type="button"
      className="guestEvRow"
      onClick={handleClick}
      title={item.event_title}
      aria-label={badge ? `${item.event_title} ${badge.label}` : item.event_title}
      disabled={!item.invite_token}
    >
      <div className="ger-titleRow">
        <div className="ger-title">{item.event_title}</div>
        {badge && (
          <span className={`status-badge ${badge.className}`}>
            {badge.label}
          </span>
        )}
      </div>

      <div className="ger-meta">{formatPeriod(item)}</div>

      <div className="ger-place">
        <span className="place-marker" aria-hidden>📍</span>
        <span>{item.event_location || '-'}</span>
      </div>

      {item.host_name && (
        <div className="ger-host">
          <span className="host-label">運用者:</span>
          <span className="host-value">{item.host_name}</span>
        </div>
      )}
    </button>
  );
};

/* ===============================================================
   メインコンポーネント
   =============================================================== */

const GuestDashboard = () => {
  const nav = useNavigate();

  /* ---------------------------------------------------------------
     State定義
     --------------------------------------------------------------- */
  
  // イベント一覧関連
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // フィルター関連
  const [q, setQ] = useState('');  // フリーワード検索クエリ

  /* ---------------------------------------------------------------
     API呼び出し関数
     --------------------------------------------------------------- */
  
  /**
   * イベント一覧を取得
   */
  const fetchList = async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await axios.get(`${API_URL}/api/enduser/events/`);
      if (r.data?.success) {
        setList(r.data.items || []);
      } else {
        setErr('一覧の取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr('一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------
     イベントハンドラ
     --------------------------------------------------------------- */
  
  /**
   * イベント行クリック時の処理（詳細画面へ遷移）
   * @param {string} token - 招待トークン
   */
  const handleRowClick = (token) => {
    nav(`/enduser/event/${token}`);
  };

  /* ---------------------------------------------------------------
     useMemo（計算結果のメモ化）
     --------------------------------------------------------------- */
  
  /**
   * フィルタリングされたイベント一覧
   * タイトル、場所、状態、運用者、期間でフリーワード検索
   */
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;

    return list.filter(ev => {
      const period = formatPeriod(ev);
      const hay = [
        ev.event_title || '',
        ev.event_location || '',
        ev.status || '',
        ev.host_name || '',
        period || '',
      ].join(' ').toLowerCase();

      return hay.includes(needle);
    });
  }, [list, q]);

  /* ---------------------------------------------------------------
     useEffect（副作用処理）
     --------------------------------------------------------------- */
  
  // 初回ロード時にイベント一覧を取得
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------------------------------------------
     レンダリング関数
     --------------------------------------------------------------- */
  
  /**
   * エラーメッセージを表示
   */
  const renderError = () => (
    err && <div className="error-alert">{err}</div>
  );

  /**
   * リストヘッダー（タイトル、検索、再読込ボタン）を表示
   */
  const renderListHeader = () => (
    <div className="listHead">
      <div className="listTitle">参加可能イベント一覧</div>
      
      {/* フリーワード検索 */}
      <input
        className="listSearchInput"
        placeholder="タイトル / 場所 / 状態 / 運用者 などで検索"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      
      {/* 操作ボタン */}
      <div className="listOps">
        <button className="btn" onClick={fetchList} disabled={loading}>
          {loading ? '更新中…' : '再読込'}
        </button>
      </div>
    </div>
  );

  /**
   * リストボディ（イベント一覧）を表示
   */
  const renderListBody = () => (
    <div className="listBody">
      {/* データなしの場合 */}
      {filtered.length === 0 && !loading && (
        <div className="empty">イベントがありません</div>
      )}
      
      {/* イベント一覧 */}
      {filtered.map(it => (
        <Row
          key={it.event_id}
          item={it}
          onClick={handleRowClick}
        />
      ))}
    </div>
  );

  /**
   * リストカード全体を表示
   */
  const renderListCard = () => (
    <div className="listCard">
      {renderListHeader()}
      {renderListBody()}
    </div>
  );

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */
  return (
    <div className="guestDash">
      {/* エラー表示 */}
      {renderError()}
      
      {/* イベントリスト */}
      {renderListCard()}
    </div>
  );
};

export default GuestDashboard;